import React, { useEffect, useState, useRef, useCallback } from "react";
import { ShieldCheck, X, FileUp, FileCheck2, Trash2, Download, ClipboardList, Plus } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, API } from "../lib/api";
import { ASSET_TYPES, CLAIM_STATUSES, CLAIM_MAP } from "../lib/assets";
import DateInput from "./DateInput";

/* ── Warranty claims log (shown when editing an existing asset) ──────────── */
function ClaimsSection({ assetId }) {
  const [claims, setClaims] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState({ claim_date: new Date().toISOString().slice(0, 10), description: "", reference: "", status: "open" });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/assets/${assetId}/claims`);
      setClaims(data.claims || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load claims");
    } finally {
      setLoading(false);
    }
  }, [assetId]);

  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!draft.description.trim()) { toast.error("Describe the fault / reason"); return; }
    try {
      await api.post(`/assets/${assetId}/claims`, draft);
      toast.success("Claim logged");
      setDraft({ claim_date: new Date().toISOString().slice(0, 10), description: "", reference: "", status: "open" });
      setAdding(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not log claim");
    }
  };

  const patch = async (cid, body) => {
    try {
      await api.patch(`/claims/${cid}`, body);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Update failed");
    }
  };

  const del = async (cid) => {
    if (!window.confirm("Delete this claim?")) return;
    try { await api.delete(`/claims/${cid}`); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Delete failed"); }
  };

  return (
    <div className="border-t border-[var(--line)] pt-4" data-testid="asset-claims-section">
      <div className="flex items-center justify-between mb-2">
        <label className="label flex items-center gap-1.5"><ClipboardList size={12} /> Warranty claims · {claims.length}</label>
        {!adding && (
          <button type="button" onClick={() => setAdding(true)} className="btn-ghost text-xs" data-testid="claim-add-btn">
            <Plus size={12} /> Log claim
          </button>
        )}
      </div>

      {adding && (
        <div className="p-3 rounded border border-[var(--line)] bg-[var(--bg-2)] space-y-2 mb-3" data-testid="claim-add-form">
          <div className="grid grid-cols-2 gap-2">
            <DateInput className="input-swiss text-xs" value={draft.claim_date}
              onChange={(e) => setDraft({ ...draft, claim_date: e.target.value })} data-testid="claim-date" />
            <input className="input-swiss text-xs" placeholder="RMA / case ref" value={draft.reference}
              onChange={(e) => setDraft({ ...draft, reference: e.target.value })} data-testid="claim-ref" />
          </div>
          <textarea className="input-swiss text-xs" rows={2} placeholder="Fault / reason for claim" value={draft.description}
            onChange={(e) => setDraft({ ...draft, description: e.target.value })} data-testid="claim-description" />
          <div className="flex justify-end gap-2">
            <button type="button" onClick={() => setAdding(false)} className="btn-ghost text-xs">Cancel</button>
            <button type="button" onClick={add} className="btn-primary text-xs" data-testid="claim-save-btn">Save claim</button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="muted text-xs">Loading…</div>
      ) : claims.length === 0 && !adding ? (
        <div className="muted text-xs py-2">No claims raised against this asset.</div>
      ) : (
        <div className="space-y-2">
          {claims.map((c) => {
            const st = CLAIM_MAP[c.status] || CLAIM_STATUSES[0];
            return (
              <div key={c.id} className="p-2.5 rounded border border-[var(--line)]" data-testid={`claim-row-${c.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="text-[12px] font-medium">{c.description || "—"}</div>
                    <div className="text-[10px] muted font-mono">{c.claim_date || "no date"}{c.reference ? ` · ${c.reference}` : ""}{c.cost_recovered_gbp ? ` · £${c.cost_recovered_gbp} recovered` : ""}</div>
                    {c.outcome && <div className="text-[11px] mt-1">Outcome: {c.outcome}</div>}
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    <select value={c.status} onChange={(e) => patch(c.id, { status: e.target.value })}
                      className="text-[10px] font-bold rounded px-1.5 py-1 border-0 cursor-pointer"
                      style={{ background: st.c + "22", color: st.c }} data-testid={`claim-status-${c.id}`}>
                      {CLAIM_STATUSES.map((s) => <option key={s.k} value={s.k}>{s.l}</option>)}
                    </select>
                    <button type="button" onClick={() => del(c.id)} className="btn-ghost p-1 text-[#C0392B]" data-testid={`claim-delete-${c.id}`}><Trash2 size={12} /></button>
                  </div>
                </div>
                {(c.status === "resolved" || c.status === "approved") && (
                  <div className="grid grid-cols-2 gap-2 mt-2">
                    <input className="input-swiss text-[11px]" placeholder="Outcome notes" defaultValue={c.outcome || ""}
                      onBlur={(e) => e.target.value !== (c.outcome || "") && patch(c.id, { outcome: e.target.value })}
                      data-testid={`claim-outcome-${c.id}`} />
                    <input type="number" className="input-swiss text-[11px]" placeholder="£ recovered" defaultValue={c.cost_recovered_gbp ?? ""}
                      onBlur={(e) => { const v = e.target.value === "" ? null : Number(e.target.value); if (v !== (c.cost_recovered_gbp ?? null)) patch(c.id, { cost_recovered_gbp: v }); }}
                      data-testid={`claim-cost-${c.id}`} />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/**
 * Shared Register/Edit asset modal.
 * Props:
 *  - open, onClose, onSaved
 *  - projects: [{id,name}] for the site dropdown
 *  - existing: asset object when editing (null = create)
 *  - lockedProjectId: pre-selects + locks the site (project-detail tab)
 */
export default function AssetModal({ open, onClose, onSaved, projects = [], existing, lockedProjectId }) {
  const blank = {
    asset_type: "solar_panel", project_id: lockedProjectId || "", manufacturer: "", model: "",
    serial_number: "", quantity: 1, install_date: "", warranty_start_date: "",
    warranty_years: "", notes: "", warranty_doc_id: null, warranty_doc_name: null,
  };
  const [form, setForm] = useState(blank);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setForm(existing
      ? {
          asset_type: existing.asset_type || "solar_panel",
          project_id: existing.project_id || lockedProjectId || "",
          manufacturer: existing.manufacturer || "",
          model: existing.model || "",
          serial_number: existing.serial_number || "",
          quantity: existing.quantity ?? 1,
          install_date: existing.install_date || "",
          warranty_start_date: existing.warranty_start_date || "",
          warranty_years: existing.warranty_years ?? "",
          notes: existing.notes || "",
          warranty_doc_id: existing.warranty_doc_id || null,
          warranty_doc_name: existing.warranty_doc_name || null,
        }
      : { ...blank, project_id: lockedProjectId || "" });
  }, [open, existing, lockedProjectId]); // eslint-disable-line

  if (!open) return null;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const uploadDoc = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const pid = form.project_id || lockedProjectId;
      const { data } = await api.post(`/uploads${pid ? `?project_id=${pid}` : ""}`, fd);
      set("warranty_doc_id", data.id);
      set("warranty_doc_name", data.original_filename || file.name);
      toast.success("Warranty certificate attached");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const submit = async () => {
    if (!form.asset_type) { toast.error("Pick an asset type"); return; }
    setSaving(true);
    const body = {
      asset_type: form.asset_type,
      project_id: form.project_id || null,
      manufacturer: form.manufacturer || null,
      model: form.model || null,
      serial_number: form.serial_number || null,
      quantity: Number(form.quantity) || 1,
      install_date: form.install_date || null,
      warranty_start_date: form.warranty_start_date || null,
      warranty_years: form.warranty_years === "" ? null : Number(form.warranty_years),
      notes: form.notes || null,
      warranty_doc_id: form.warranty_doc_id,
      warranty_doc_name: form.warranty_doc_name,
    };
    try {
      if (existing) await api.patch(`/assets/${existing.id}`, body);
      else await api.post(`/assets`, body);
      toast.success(existing ? "Asset updated" : "Asset registered");
      onSaved();
      onClose();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="asset-modal">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-5 py-4 border-b border-[var(--line)] sticky top-0 bg-white">
          <h2 className="font-display text-lg font-bold flex items-center gap-2">
            <ShieldCheck size={18} className="text-[var(--accent)]" />
            {existing ? "Edit asset" : "Register asset"}
          </h2>
          <button onClick={onClose} className="btn-ghost p-1" data-testid="asset-modal-close"><X size={18} /></button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <label className="label">Asset type</label>
            <div className="grid grid-cols-3 gap-2 mt-1">
              {ASSET_TYPES.map((t) => {
                const active = form.asset_type === t.key;
                return (
                  <button key={t.key} type="button" onClick={() => set("asset_type", t.key)}
                    className={`flex flex-col items-center gap-1 py-2.5 rounded border text-[11px] font-semibold transition-colors ${
                      active ? "bg-[var(--accent)] text-white border-[var(--accent)]"
                             : "bg-white border-[var(--line)] hover:border-[var(--accent)]"}`}
                    data-testid={`asset-type-${t.key}`}>
                    <t.icon size={16} /> {t.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label">Manufacturer</label>
              <input className="input-swiss mt-1" value={form.manufacturer}
                onChange={(e) => set("manufacturer", e.target.value)}
                placeholder="e.g. JinkoSolar" data-testid="asset-manufacturer" />
            </div>
            <div>
              <label className="label">Model</label>
              <input className="input-swiss mt-1" value={form.model}
                onChange={(e) => set("model", e.target.value)}
                placeholder="e.g. Tiger Neo 440W" data-testid="asset-model" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label">Serial number</label>
              <input className="input-swiss mt-1 font-mono" value={form.serial_number}
                onChange={(e) => set("serial_number", e.target.value)}
                placeholder="SN-000123" data-testid="asset-serial" />
            </div>
            <div>
              <label className="label">Quantity</label>
              <input type="number" min="1" className="input-swiss mt-1" value={form.quantity}
                onChange={(e) => set("quantity", e.target.value)} data-testid="asset-quantity" />
            </div>
          </div>

          {!lockedProjectId && (
            <div>
              <label className="label">Site (optional)</label>
              <select className="input-swiss mt-1" value={form.project_id}
                onChange={(e) => set("project_id", e.target.value)} data-testid="asset-project">
                <option value="">— Unassigned —</option>
                {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </div>
          )}

          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="label">Install date</label>
              <DateInput className="input-swiss mt-1" value={form.install_date}
                onChange={(e) => set("install_date", e.target.value)} data-testid="asset-install-date" />
            </div>
            <div>
              <label className="label">Warranty start</label>
              <DateInput className="input-swiss mt-1" value={form.warranty_start_date}
                onChange={(e) => set("warranty_start_date", e.target.value)} data-testid="asset-warranty-start" />
            </div>
            <div>
              <label className="label">Warranty (yrs)</label>
              <input type="number" step="0.5" min="0" className="input-swiss mt-1" value={form.warranty_years}
                onChange={(e) => set("warranty_years", e.target.value)}
                placeholder="25" data-testid="asset-warranty-years" />
            </div>
          </div>
          <p className="text-[11px] muted -mt-1">
            Warranty status is derived from the start date (or install date) + term. Leave the term blank for "Not in Warranty".
          </p>

          <div>
            <label className="label">Warranty certificate</label>
            {form.warranty_doc_id ? (
              <div className="mt-1 flex items-center gap-2 p-2.5 rounded border border-[var(--line)] bg-[var(--bg-2)]" data-testid="asset-doc-attached">
                <FileCheck2 size={15} className="text-[#15934F] shrink-0" />
                <span className="text-xs font-medium truncate flex-1">{form.warranty_doc_name || "Certificate"}</span>
                <a href={`${API}/files/${form.warranty_doc_id}`} target="_blank" rel="noopener noreferrer"
                   className="btn-ghost p-1.5" title="View" data-testid="asset-doc-view"><Download size={13} /></a>
                <button onClick={() => { set("warranty_doc_id", null); set("warranty_doc_name", null); }}
                        className="btn-ghost p-1.5 text-[#C0392B]" title="Remove" data-testid="asset-doc-remove"><Trash2 size={13} /></button>
              </div>
            ) : (
              <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading}
                      className="btn-ghost text-xs mt-1" data-testid="asset-doc-upload">
                <FileUp size={13} /> {uploading ? "Uploading…" : "Attach PDF / image"}
              </button>
            )}
            <input ref={fileRef} type="file" accept=".pdf,.png,.jpg,.jpeg,.webp" className="hidden"
                   onChange={uploadDoc} data-testid="asset-doc-input" />
          </div>

          <div>
            <label className="label">Notes</label>
            <textarea className="input-swiss mt-1" rows={2} value={form.notes}
              onChange={(e) => set("notes", e.target.value)} data-testid="asset-notes" />
          </div>

          {existing && <ClaimsSection assetId={existing.id} />}
        </div>
        <div className="flex justify-end gap-2 px-5 py-4 border-t border-[var(--line)] sticky bottom-0 bg-white">
          <button onClick={onClose} className="btn-ghost" data-testid="asset-cancel">Cancel</button>
          <button onClick={submit} disabled={saving} className="btn-primary" data-testid="asset-save">
            {saving ? "Saving…" : existing ? "Save changes" : "Register asset"}
          </button>
        </div>
      </div>
    </div>
  );
}
